/**
 * Retourne un *string* de la forme "YYYY-MM-DD" à partir de Year, Month, Day.
 * @param year
 * @param month
 * @param day
 */
export const createDateString = (year: number, month: number, day: number) => {
	return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
};

/**
 * Retourne un *string* de la forme "DD/MM/YYYY" à partir d'une date.
 * @param date
 * @param locale
 */
export const getLocaleDateString = (date: Date, locale = "en-US") => {
	return date.toLocaleDateString(locale);
};

/**
 * Retourne un *string* de la forme "DD/MM/YYYY à HH:mm:ss" à partir d'une date.
 * @param date
 * @param locale
 */
export const getLocaleString = (date: Date, locale = "en-US") => {
	return date.toLocaleString(locale);
};

/**
 * Retourne un *string* de la forme "DD/MM/YYYY à HH:mm:ss" à partir d'une date string.
 * @param dateString
 * @param locale
 */
export const getLocaleStringFromDateString = (dateString: string, locale = "en-GB") => {
	return getLocaleString(new Date(Date.parse(dateString)), locale);
};

/**
 * Retourne un *string* de la forme "Aug 22nd 2020 hh:mm:ss UTC" à partir d'une date string.
 */
export const getLocaleStringFromDateStringWithOptions = (dateString: string, options?: Intl.DateTimeFormatOptions, locale = "en-GB") => {
	return (new Date(Date.parse(dateString))).toLocaleString(locale, options);
};


/**
 * Retourne un *string* de la forme "2019-03-20T11:24:06.065Z".
 * @param date
 */
export const getDateTimeString = (date: Date) => {
	return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();
};


/**
 * Retourne un *string* de la forme "lundi 1 janvier 2000" à partir d'une date.
 * @param date
 * @param locale
 */
export const getElegantDateString = (date: Date, locale = "en-US") => {
	return date.toLocaleDateString(locale, {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric"
	});
};

/**
 * Retourne un *string* de la forme "lundi 1 janvier 2000" à partir d'une string de la forme yyyy-MM-dd.
 */
export const getElegantDateStringFromString = (dateAsString: string, locale = "en-US") => {
	return (new Date(`${dateAsString}T00:00:00`)).toLocaleDateString(locale, {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric"
	});
};

/**
 * Retourne le mois traduit dans la {locale} à partir du numéro du mois.
 * @param monthNumber
 * @param locale
 */
export const getMonthName = (monthNumber: number, locale = "en-US") => {
	return (new Date(`2000-${monthNumber.toString().padStart(2, "0")}-01T00:00:00`)).toLocaleDateString(locale, {
		month: "long"
	});
};


/**
 * Retourne un *string* de la forme "janvier" à partir d'une date.
 * @param date
 * @param locale
 */
export const getMonthStringFromDate = (date: Date, locale = "en-US") => {
	return date.toLocaleDateString(locale, {
		month: "long"
	});
};

/**
 * Retourne le timestamp en nombre de jours (arrondi en floor).
 */
export const getTimestampInDays = (timestampInMS: number) => {
	return Math.floor((((timestampInMS / 1000) / 60) / 60) / 24);
};

/**
 * Retourne un *string* de la forme "2019-03-20".
 */
export const getDateStringFromDateTimeAsString = (dateTimeAsString: string) => {
	return dateTimeAsString.split("T")[0];
};

/**
 * Retourne un *string* de la forme "2019-03-20".
 * @param date
 */
export const getDateString = (date: Date) => {
	return getDateStringFromDateTimeAsString(getDateTimeString(date));
};
/**
 * Retourne un *string* de la forme "2019-03".
 * @param date
 */
export const getYearMonthString = (date: Date) => {
	const dateStringSplit = getDateString(date).split("-");
	return `${dateStringSplit[0]}-${dateStringSplit[1]}`;
};
/**
 * Retourne un *string* de la forme "11:24:06.065Z".
 * @param date
 */
export const getTimeString = (date: Date) => {
	return getDateTimeString(date).split("T")[1];
};
/**
 * Retourne un *string* de la forme "11:24".
 * @param date
 */
export const getShortTimeString = (date: Date) => {
	const timeSplit = getTimeString(date).split(":");
	return `${timeSplit[0]}:${timeSplit[1]}`;
};

/**
 * Ajout un nombre de minutes à un time de la forme "11:24".
 * @param time
 * @param nbMinutesToAdd
 */
export const addMinutes = (time: string, nbMinutesToAdd: number) => {
	const timeSplit = time.split(":");
	const hour = parseInt(timeSplit[0], 10);
	const minute = parseInt(timeSplit[1], 10);

	const rawMinuteAfterAddition = minute + nbMinutesToAdd;
	return `${(hour + Math.floor(rawMinuteAfterAddition / 60)).toString().padStart(2, "0")}:${(rawMinuteAfterAddition % 60).toString().padStart(2, "0")}`;
};

/**
 * Retourne un affichage *string* intelligent d'une durée en heures ou en minutes.
 * @param minutes
 */
export const smartDisplayMinutes = (minutes: number) => {
	if (minutes == null) {
		return "-";
	}
	if (minutes >= 60) {
		const remainingSeconds = minutes % 60;
		return `${Math.floor(minutes / 60)}h` + (remainingSeconds > 0 ? remainingSeconds.toString().padStart(2, "0") : "");
	}
	return `${minutes} min`;
};

/**
 * Retourne un affichage *string* intelligent d'une durée en jours (Y year, M months, D days).
 * @param numberOfDays
 */
export const smartDisplayDays = (numberOfDays: number) => {
	if (numberOfDays == 0) {
		return "today";
	}

	const years = Math.floor(numberOfDays / 365);
	const months = Math.floor(numberOfDays % 365 / 30);
	const days = Math.floor(numberOfDays % 365 % 30);

	const result = [];
	if (years > 0) {
		result.push(years + (years == 1 ? " year" : " years"));
	}
	if (months > 0) {
		result.push(months + (months == 1 ? " month" : " months"));
	}
	if (days > 0) {
		result.push(days + (days == 1 ? " day" : " days"));
	}

	return result.join(", ");
};

/**
 * Retourne la différence en minutes entre *date1* et *date2* telles que date1 < date2.
 */
export const differenceInMinutesBetweenDates = (date1: Date, date2: Date) => {
	return Math.round(((date2.getTime() - date1.getTime()) / 1000) / 60); // minutes
};

/**
 * Retourne la différence en jours entre *date1* et *date2* telles que date1 < date2.
 */
export const differenceInDaysBetweenDates = function (date1: Date, date2: Date) {
	return Math.floor((Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate()) - Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate())) / (1000 * 60 * 60 * 24));
};

/**
 * Retourne la différence en minutes entre *hh1:mm1* et *hh2:mm2* telles que hh1:mm1 < hh2:mm2.
 */
export const differenceInMinutesBetweenHoursMinutes = (hm1: string, hm2: string) => {
	const h1 = parseInt(hm1.split(":")[0], 10);
	const m1 = parseInt(hm1.split(":")[1], 10);
	const h2 = parseInt(hm2.split(":")[0], 10);
	const m2 = parseInt(hm2.split(":")[1], 10);
	return (60 * h2 + m2) - (60 * h1 + m1);
};

/**
 * Fonction de tri par date croissant si a < b.
 * @param a
 * @param b
 */
export const sortByDate = (a: Date, b: Date) => {
	return a > b ? -1 : a < b ? 1 : 0;
};


/**
 * Retourne la date minimale d'un array de dates.
 */
export const getMinDate = (dates: Date[]) => {
	return dates.reduce(function (a, b) {
		return a < b ? a : b;
	});
};

/**
 * Retourne la date maximale d'un array de dates.
 */
export const getMaxDate = (dates: Date[]) => {
	return dates.reduce(function (a, b) {
		return a > b ? a : b;
	});
};

/**
 * Retourne la durée formatée entre maintenant et la date passée en paramètre.
 * @param date
 * @param referenceDate
 * @param onlyMinutes
 */
export const getTimeSince = (date: Date, referenceDate: Date = new Date(), onlyMinutes = false) => {
	if (typeof date !== "object") {
		date = new Date(date);
	}

	let seconds = Math.floor((+referenceDate - +date) / 1000);
	if (seconds < 0) {
		seconds = 0;
	}
	let intervalType;

	let interval = Math.floor(seconds / 31536000);
	if (interval >= 1) {
		intervalType = "year";
	}
	else {
		interval = Math.floor(seconds / 2592000);
		if (interval >= 1) {
			intervalType = "month";
		}
		else {
			interval = Math.floor(seconds / 86400);
			if (interval >= 1) {
				intervalType = "day";
			}
			else {
				interval = Math.floor(seconds / 3600);
				if (interval >= 1) {
					intervalType = "hour";
				}
				else {
					interval = Math.floor(seconds / 60);
					if (interval >= 1 || onlyMinutes) {
						intervalType = "minute";
					}
					else {
						interval = seconds;
						intervalType = "second";
					}
				}
			}
		}
	}

	if (interval > 1 || interval === 0) {
		intervalType += "s";
	}


	if (onlyMinutes) {

	}

	return (interval > 0 ? interval : "< 1") + " " + intervalType;
};